import MainLayout from "@/shared/components/MainLayout";
import Header from "@/shared/components/ui/Header";
import { Button, Dialog, DialogContent } from "@/shared";
import { Check, Search, Receipt } from "lucide-react";
import useCreateExpenseVoucherController from "@/hooks/useCreateExpenseVoucherController";
import DatePicker from "@/shared/components/ui/DatePicker";
import { Link } from "react-router-dom";

const CreateExpenseVoucher = () => {
    const {
        t,
        projects,
        searchTerm,
        accountResults,
        isSearchingAccounts,
        selectedAccount,
        formData,
        isSubmitted,
        isUpdating,
        showSuccessModal,
        handleSearchChange,
        selectAccount,
        handleFormInputChange,
        createExpenseVoucher,
        resetForm,
    } = useCreateExpenseVoucherController();

    return (
        <MainLayout>
            <Header title={t("create_expense_voucher")} />

            <div className="max-w-2xl mx-auto p-4 space-y-6">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 ">

                    <div className="p-4 border-b border-slate-100 bg-slate-50/80 flex items-center justify-between">
                        <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
                            <span className="w-2 h-5 bg-violet-600 rounded-full"></span>
                            {t("voucher_details")}
                        </h2>
                        <Receipt className="w-5 h-5 text-slate-400" />
                    </div>

                    <div className="p-5 space-y-4">
                        <div className="relative">
                            <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5 mr-1">
                                {t("account")}
                            </label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => handleSearchChange(e.target.value)}
                                    placeholder={t("search_account_placeholder")}
                                    className={`w-full bg-slate-50/50 border rounded-xl pl-9 pr-3 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-violet-500/20 focus:border-violet-600 outline-none transition-all ${isSubmitted && !selectedAccount
                                        ? "border-red-500 ring-1 ring-red-500"
                                        : "border-slate-200"
                                        }`}
                                />
                            </div>

                            {isSearchingAccounts && (
                                <p className="text-xs text-slate-400 mt-1 mr-1">{t("searching")}</p>
                            )}

                            {accountResults.length > 0 && (
                                <div className="absolute z-30 w-full bg-white border border-slate-200 shadow-xl rounded-xl mt-1 max-h-48 overflow-auto divide-y divide-slate-100">
                                    {accountResults.map((acc) => (
                                        <div
                                            key={acc.account_id}
                                            onClick={() => selectAccount(acc)}
                                            className="px-4 py-2.5 hover:bg-violet-50/60 hover:text-violet-700 cursor-pointer text-sm transition-colors flex justify-between items-center"
                                        >
                                            <span className="font-medium">{acc.account_name}</span>
                                            <span className="text-xs text-slate-400 font-mono">#{acc.account_id}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {isSubmitted && !selectedAccount && (
                                <p className="text-xs text-red-500 mt-1 mr-1">{t("please_select_account")}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5 mr-1">
                                {t("date")}
                            </label>
                            <DatePicker
                                // enableTime
                                id="pp-from"
                                label=""
                                placeholder={t("Select a date")}
                                defaultDate={formData.date}
                                error={isSubmitted && !formData.date}
                                onChange={(_, dateStr) => handleFormInputChange("date", dateStr)}
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5 mr-1">
                                {t("project")}
                            </label>
                            <select
                                value={formData.projectId}
                                onChange={(e) => handleFormInputChange("projectId", e.target.value)}
                                className={`w-full bg-slate-50/50 border rounded-xl px-3 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-violet-500/20 focus:border-violet-600 outline-none transition-all ${isSubmitted && !formData.projectId
                                    ? "border-red-500 ring-1 ring-red-500"
                                    : "border-slate-200"
                                    }`}
                            >
                                <option value="">{t("select_project")}</option>
                                {projects.map((p) => (
                                    <option key={p.type_id} value={p.business_id}>
                                        {p.type_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5 mr-1">
                                {t("amount")}
                            </label>
                            <input
                                type="number"
                                min="0"
                                value={formData.amount}
                                onChange={(e) => handleFormInputChange("amount", e.target.value)}
                                className={`w-full bg-slate-50/50 border rounded-xl px-3 py-2.5 text-sm text-center font-bold text-slate-800 focus:bg-white focus:ring-2 focus:ring-violet-500/20 focus:border-violet-600 outline-none transition-all ${isSubmitted && !(Number(formData.amount) > 0)
                                    ? "border-red-500 ring-1 ring-red-500"
                                    : "border-slate-200"
                                    }`}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex justify-end">
                    <Button
                        onClick={createExpenseVoucher}
                        disabled={isUpdating}
                        loading={isUpdating}
                        className="min-w-[140px] h-11 bg-slate-900 hover:bg-violet-700 text-white font-semibold rounded-xl transition-all duration-300 shadow-sm hover:shadow-violet-200"
                    >
                        {t("insert")}
                    </Button>
                </div>
            </div>

            <Dialog open={showSuccessModal} onOpenChange={(open) => { if (!open) resetForm(); }}>
                <DialogContent>
                    <div className="bg-white p-8 rounded-2xl shadow-xl max-w-sm w-full text-center space-y-6">
                        <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto border border-emerald-100">
                            <Check className="w-7 h-7" />
                        </div>
                        <h2 className="text-lg font-bold text-slate-800">
                            {t("voucher_created_successfully")}
                        </h2>
                        <div className="flex flex-col gap-2.5">
                            <Button
                                onClick={resetForm}
                                className="w-full h-11 bg-slate-900 hover:bg-violet-700 text-white rounded-xl transition-all"
                            >
                                {t("create_new_voucher")}
                            </Button>

                            <Button
                                asChild
                                variant="outline"
                                className="w-full h-11 border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl transition-all"
                            >
                                <Link to="/expenses">
                                    {t("back_to_expenses")}
                                </Link>
                            </Button>

                            <Button
                                asChild
                                variant="outline"
                                className="w-full h-11 border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl transition-all"
                            >
                                <Link to="/sales_profits">
                                    {t("back_to_item_sales_report")}
                                </Link>
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </MainLayout>
    );
};

export default CreateExpenseVoucher;